import { useState, useRef, useCallback, useEffect } from "react";
import { HotTable } from "@handsontable/react";
import { registerAllModules } from "handsontable/registry";
import {
  alpha,
  Box,
  Button,
  darken,
  lighten,
  Paper,
  useTheme,
} from "@mui/material";
import LoadingPage from "../../loadingPage";
import "handsontable/styles/ht-theme-main.css";
import "handsontable/dist/handsontable.full.min.css";
import { Download } from "@mui/icons-material";

registerAllModules();

// --- Ícones SVG ---
const EDIT_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/></svg>`;
const DELETE_ICON_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"/></svg>`;

const customStyles = (theme) => `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  :root {
    --bg-app: ${theme.palette.background.paper};
    --bg-card: ${theme.palette.background.paper};
    --text-primary: ${theme.palette.text.primary};
    --text-secondary: ${theme.palette.text.secondary};
    --border-subtle: ${theme.palette.stroke.main};
    --row-odd: ${theme.palette.background.card};
    --row-even: ${theme.palette.background.paper};
    --row-hover: ${theme.palette.background.default};
    --header-bg: ${theme.palette.background.paper};
    --header-text: ${theme.palette.text.secondary};
    --input-bg:${theme.palette.background.textField};
    --error-text: ${theme.palette.error.main};
    --header-active-bg: ${theme.palette.background.paper};
    --header-active-text: ${theme.palette.text.primary};
    --action-edit: ${theme.palette.text.primary};
    --action-delete: ${theme.palette.error.main};
}
  .custom-table-wrapper .handsontable,
  .custom-table-wrapper .htCore,
  .custom-table-wrapper .wtHider,
  .custom-table-wrapper .wtSpreader,
  .custom-table-wrapper .ht_clone_top,
  .custom-table-wrapper .ht_clone_left,
  .custom-table-wrapper .ht_clone_corner,
  .custom-table-wrapper .ht_master .wtHolder {
    background-color: var(--bg-app) !important;
    border: none !important; 
  }
    
  .custom-table-wrapper { font-family: 'Inter', sans-serif; transition: background-color 0.3s ease; }
  .custom-table-wrapper .handsontable { border: none !important; overflow: hidden; }
  .ht_master .wtHider { border: none !important; color: var(--text-primary) !important; }

  .custom-table-wrapper .handsontable th {
    background-color: var(--header-bg);
    color: var(--header-text);
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid var(--border-subtle);
    border-top: none; border-right: none !important; border-left: none !important;
    padding: 0px 5px; text-align: left; vertical-align: middle;
  }

  .custom-table-wrapper .handsontable td {
    border-top: 1px solid var(--border-subtle) !important;
    border-left: none !important;
    border-right: none !important;
    border-bottom: 1px solid var(--border-subtle) !important;
    background-color: transparent !important;
    color: var(--text-primary) !important;
  }

  .error-cell { color: var(--error-text) !important; font-weight: 500; }

  .custom-table-wrapper .handsontable tbody tr:nth-of-type(odd) td,
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(odd) th { background-color: var(--row-odd) !important; }
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(even) td,
  .custom-table-wrapper .handsontable tbody tr:nth-of-type(even) th { background-color: var(--row-even) !important; }
  .custom-table-wrapper .handsontable tbody tr:hover td,
  .custom-table-wrapper .handsontable tbody tr:hover th { background-color: var(--row-hover) !important; }

  .htDropdownMenu, .htAutocomplete { background: var(--bg-card); padding: 4px; border: 1px solid var(--border-subtle); }
  .htDropdownMenu table.htCore td, .htAutocomplete table.htCore td {
     background-color: var(--bg-card) !important; color: var(--text-primary) !important; border: none; border-radius: 4px; padding: 8px 12px;
  }

  .handsontableInput { background-color: var(--input-bg) !important; color: var(--text-primary) !important; font-family: 'Inter', sans-serif !important; font-size: 13px !important; }
  .handsontableInputHolder { background-color: var(--input-bg) !important; }

  /* Chips */
  .ht-chip {
    display: inline-flex;
    align-items: center;
    justify-content: center; height: 20px;
    padding: 0 10px;
    border-radius: 4px;
    font-size: 0.55rem;
    font-weight: 900;
    cursor: pointer; border: 1px solid transparent;
    transition: all 0.2s;
  }
  .ht-chip-success {
    background-color: ${alpha(theme.palette.sucess.main, 0.1)};
    color:  ${theme.palette.mode === "dark" ? lighten(theme.palette.sucess.main, 0.2) : darken(theme.palette.sucess.main, 0.3)};
    border-color: ${theme.palette.sucess.main};
    border-radius: 10px;
    }
  
  .ht-chip-error {
    background-color: ${alpha(theme.palette.error.main, 0.2)};
    color:  ${theme.palette.mode === "dark" ? lighten(theme.palette.error.main, 0.2) : darken(theme.palette.error.main, 0.3)};
    border: "0.1px solid ";
    border-color: ${theme.palette.error.main};
    border-radius: 10px;
  }
  .ht-chip-null {
    background-color:${alpha(theme.palette.chip.arquive, 0.3)};
    color:  ${theme.palette.mode === "dark" ? lighten(theme.palette.text.secondary, 0.6) : darken(theme.palette.text.secondary, 0.6)};
    border-color: var(--text-secondary);
    border-radius: 10px;
    opacity: 0.7;
  }
  .ht-chip-null:hover { opacity: 1; border-color: var(--text-secondary); }

  .action-cell-container {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 8px;
      height: 100%;
      width: 100%;
  }

  .action-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      background: transparent;
      border: none;
      cursor: pointer;
      padding: 4px;
      border-radius: 50%;
      transition: background-color 0.2s;
  }
  .action-btn:hover { background-color: rgba(0,0,0,0.05); }
  .action-btn.edit { color: var(--action-edit); }
  .action-btn.delete { color: var(--action-delete); }

  .custom-table-wrapper .handsontable th.ht__highlight {
    background-color: var(--header-active-bg) !important;
    color: var(--header-active-text) !important;
    border-bottom: 2px solid var(--header-active-text);
    transition: background-color 0.2s ease;
  }

  .custom-table-wrapper.view-mode .htAutocompleteArrow { display: none !important; }
  .custom-table-wrapper.view-mode .htAutocomplete { padding-right: 5px !important; }
  .custom-table-wrapper input[type="checkbox"] {
    appearance: none;
    -webkit-appearance: none;
    width: 14px;
    height: 14px;
    border: 2px solid;
    border-radius: 3px;
    background-color: transparent;
    cursor: pointer;
    position: relative;
    border-color: ${theme.palette.text.main}80;
}
.custom-table-wrapper input[type="checkbox"]:hover {
  border-color: var(--header-active-text);
  background-color: ${alpha(theme.palette.primary.main, 0.08)};
}
  .custom-table-wrapper input[type="checkbox"]:checked {
  background-color: var(--header-active-text);
  border-color: var(--header-active-text);
}
.custom-table-wrapper input[type="checkbox"]:checked::after {
  content: "";
  position: absolute;
  top: 1px;
  left: 4px;
  width: 4px;
  height: 8px;
  border: solid ${theme.palette.background.paper};
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.custom-table-wrapper .handsontable,
.custom-table-wrapper .handsontable thead,
.custom-table-wrapper .handsontable thead tr:first-child th {
  border-top: none !important;
}
`;

export default function NetworkTable({
  columns,
  rows,
  isEditable = false,
  setIsEditable,
  equipmentMap = {},
  onEditRow,
  onDeleteRow,
  loadingTable,
}) {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const hotRef = useRef(null);

  const [updateTrigger, setUpdateTrigger] = useState(0);

  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add("dark-theme");
      document.body.style.backgroundColor = theme.palette.background.default;
    } else {
      document.body.classList.remove("dark-theme");
      document.body.style.backgroundColor = theme.palette.background.default;
    }
    document.body.style.backgroundColor = theme.palette.background.default;
  }, [isDarkMode, theme]);

  // --- RENDERIZADOR DE AÇÕES ---
  const actionsRenderer = (
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  ) => {
    while (td.firstChild) {
      td.removeChild(td.firstChild);
    }

    const container = document.createElement("div");
    container.className = "action-cell-container";

    const editBtn = document.createElement("div");
    editBtn.className = "action-btn edit";
    editBtn.innerHTML = EDIT_ICON_SVG;
    editBtn.dataset.action = "edit";
    editBtn.title = "Editar";

    const deleteBtn = document.createElement("div");
    deleteBtn.className = "action-btn delete";
    deleteBtn.innerHTML = DELETE_ICON_SVG;
    deleteBtn.dataset.action = "delete";
    deleteBtn.title = "Excluir";

    container.appendChild(editBtn);
    container.appendChild(deleteBtn);
    td.appendChild(container);
    td.style.textAlign = "center";
    td.style.verticalAlign = "middle";
  };

  // --- RENDERIZADOR DE CHIPS UNIFICADO (PING OU SNMP) ---
  // Agora renderiza apenas UM chip baseado na propriedade da coluna
  const chipRenderer = (
    instance,
    td,
    row,
    col,
    prop,
    value,
    cellProperties,
  ) => {
    while (td.firstChild) {
      td.removeChild(td.firstChild);
    }

    // Determina o Label baseado no 'prop' (checks.ping ou checks.snmp)
    let label = "UNK";
    if (prop === "checks.ping") label = "PING";
    if (prop === "checks.snmp") label = "SNMP";

    const span = document.createElement("span");
    let chipClass = "ht-chip-null";

    if (value === true) {
      chipClass = "ht-chip-success";
    } else if (value === false) {
      chipClass = "ht-chip-error";
    }

    span.className = `ht-chip ${chipClass}`;
    span.innerText = label;
    // Importante: data-prop ajuda a identificar qual campo editar no clique
    span.dataset.action = "toggle-check";
    span.dataset.prop = prop;

    td.appendChild(span);
    td.className = "chip-cell";
    td.style.textAlign = "center";
    td.style.verticalAlign = "middle";
    // td.style.padding = "4px"; // Ajuste fino para o chip preencher
  };

  // --- MANIPULADOR DE CLIQUES ---
  const handleBeforeOnCellMouseDown = (event, coords, TD) => {
    const hotInstance = hotRef.current.hotInstance;

    // Se clicou no header
    if (coords.row < 0) {
      if (coords.col === 0) {
        const currentData = hotInstance.getSourceData();
        const newState = !currentData.every((row) => row.selected);
        currentData.forEach((row, index) =>
          hotInstance.setDataAtRowProp(index, "selected", newState),
        );
        setUpdateTrigger((prev) => prev + 1);
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
      return;
    }

    // Se clicou em uma célula normal
    if (coords.row >= 0) {
      const prop = hotInstance.colToProp(coords.col);

      // Lógica Checks (Ping/SNMP separados)
      // Agora verifica se a propriedade é checks.ping OU checks.snmp
      if (prop === "checks.ping" || prop === "checks.snmp") {
        if (isEditable === false) return;

        const target = event.target;
        // Verifica se clicou no chip ou na célula contendo o chip
        if (
          target &&
          (target.dataset.action === "toggle-check" ||
            target.classList.contains("chip-cell"))
        ) {
          // Pega o valor atual direto daquela propriedade (true/false/null)
          const currentVal = hotInstance.getDataAtRowProp(coords.row, prop);

          // Inverte o valor
          hotInstance.setDataAtRowProp(coords.row, prop, !currentVal);
        }
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }

      // --- LÓGICA DE AÇÕES (EDIT/DELETE) ---
      if (prop === "actions") {
        const target = event.target.closest(".action-btn");
        if (target) {
          const action = target.dataset.action;
          const rowData = hotInstance.getSourceDataAtRow(coords.row);
          if (action === "edit") {
            if (onEditRow) onEditRow(rowData);
          } else if (action === "delete") {
            if (onDeleteRow) onDeleteRow(rowData);
          }
        }
        event.stopImmediatePropagation();
        event.preventDefault();
        return false;
      }
    }
  };

  const getColHeaders = useCallback((col) => {
    const headers = [
      "Host",
      "IP",
      "Ping", // Coluna separada
      "SNMP", // Coluna separada
      "Comunidade",
      "Porta Interface",
      "Fabricante",
      "Modelo",
      "User SSH",
      "Senha SSH",
      "Porta SSH",
      "Proxy",
      "Ações",
    ];

    return headers[col];
  }, []);

  const cellsConfig = useCallback(
    (row, col, prop) => {
      const cellProperties = {};
      const hotInstance = hotRef.current?.hotInstance;

      // Renderer de Ações
      if (prop === "actions") {
        cellProperties.readOnly = true;
        cellProperties.renderer = actionsRenderer;
        return cellProperties;
      }

      // Renderer de Chips (Ping ou SNMP)
      // Mapeia para as novas propriedades aninhadas
      if (prop === "checks.ping" || prop === "checks.snmp") {
        cellProperties.readOnly = true;
        cellProperties.renderer = chipRenderer;
        return cellProperties;
      }

      if (prop === "error_msg") {
        cellProperties.readOnly = true;
        cellProperties.className = "error-cell";
        return cellProperties;
      }

      cellProperties.readOnly = !isEditable;

      if (hotInstance && prop === "modelo") {
        const fabricante = hotInstance.getDataAtRowProp(row, "fabricante");
        cellProperties.source = equipmentMap[fabricante] || [];
      }
      return cellProperties;
    },
    [isEditable, equipmentMap],
  );
  const handleDownloadCSV = () => {
    const headers = [
      "Nome",
      "IP",
      "Fabricante",
      "Modelo",
      "Tipo interface",
      "Porta Interface",
      "Comunidade SNMP",
      "Tipo Acesso",
      "User Acesso",
      "Senha Acesso",
      "Porta Acesso",
      "Proxy",
    ];
    const snmpExample = [
      "Host exemplo 01",
      "168.0.0.1",
      "Fabricante exemplo",
      "Modelo exemplo",
      "SNMPv2",
      "161",
      "Comunidade exemplo",
      "SSH",
      "Usuario Exemplo",
      "Senha exemplo",
      "22",
      "zabbix-proxy",
    ];
    const agentExample = [
      "Host exemplo 02",
      "127.0.0.1",
      "Fabricante exemplo",
      "Modelo exemplo",
      "AGENTE",
      "10050",
      "",
      "",
      "",
      "",
      "",
      "zabbix-proxy",
    ];
    const errorExampleIp = [
      "Host exemplo 03",
      "127.0.0.",
      "Fabricante exemplo",
      "Modelo exemplo",
      "SNMPv2",
      "161",
      "Comunidade exemplo",
      "SSH",
      "Usuario Exemplo",
      "Senha exemplo",
      "22",
      "zabbix-proxy",
    ];
    const errorExampleSNMP = [
      "Host exemplo 04",
      "168.0.0.1",
      "Fabricante exemplo",
      "Modelo exemplo",
      "SNMPv2",
      "161",
      "",
      "SScH",
      "Usuario Exemplo",
      "Senha exemplo",
      "22",
      "zabbix-proxcy",
    ];

    const csvContent = [
      headers.join(","),
      snmpExample.join(","),
      agentExample.join(","),
      // errorExampleIp.join(","), // PARA TESTES::
      // errorExampleSNMP.join(","),
    ].join("\n");

    const blob = new Blob(["\ufeff", csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "MW Soluções - Template Host.csv");
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  return (
    <Paper sx={{ pt: 2, borderRadius: "16px" }}>
      <Button
        size="medium"
        variant="text"
        sx={{
          m: 0.8,
        }}
        startIcon={<Download />}
        onClick={handleDownloadCSV}
      >
        Exportar template
      </Button>

      <style>{customStyles(theme, isDarkMode)}</style>
      <Box style={{ margin: "0 auto" }}>
        <Box
          className={`custom-table-wrapper ${!isEditable ? "view-mode" : ""}`}
          sx={{
            padding: "0px",
            minHeight: "450px",
            overflow: "auto",
            position: "relative",
          }}
        >
          {loadingTable ? (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: alpha(theme.palette.background.paper, 0.7),
                zIndex: 10,
                borderRadius: "16px",
              }}
            >
              <LoadingPage mt={"5%"} />
            </Box>
          ) : (
            <HotTable
              ref={hotRef}
              data={rows}
              licenseKey="non-commercial-and-evaluation"
              rowHeaders={false}
              height="55vh"
              stretchH="all"
              rowHeights={10}
              cells={cellsConfig}
              columnSorting={true}
              afterChange={(changes, src) =>
                src !== "loadData" &&
                changes?.some(([r, p]) => p === "selected") &&
                setUpdateTrigger((p) => p + 1)
              }
              beforeOnCellMouseDown={handleBeforeOnCellMouseDown}
              colHeaders={getColHeaders}
              columns={columns}
              manualColumnResize={true}
              loading={loadingTable}
            />
          )}
        </Box>
      </Box>
    </Paper>
  );
}
